import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { globalSettingService, SupportedLanguage } from '../../api/globalSettings';
import { userSettingService } from '../../api/userSettings';
import { trackEvent } from '../../api/analyticsService';
import LanguageSelect from '../onboarding/LanguageSelect';
import { isOfferedForLearning } from '../onboarding/languageDefaults';
import '../../styles/fix-languages-prompt.css';

// A likely native language for a registration country, used only to prefill
// the picker (the user confirms it). Covers the countries the English→English
// accounts came from, plus the app's other big markets.
const COUNTRY_LANGUAGE: Record<string, string> = {
    IR: 'fa', AF: 'fa', VN: 'vi', EG: 'ar', JO: 'ar', SA: 'ar', AE: 'ar', MA: 'ar', DZ: 'ar', IQ: 'ar',
    TR: 'tr', MN: 'mn', JP: 'ja', DE: 'de', AT: 'de', ES: 'es', MX: 'es', AR: 'es', CO: 'es', FR: 'fr',
    PL: 'pl', UA: 'uk', RU: 'ru', KZ: 'ru', BY: 'ru', PT: 'pt', BR: 'pt', IT: 'it', NL: 'nl', CZ: 'cs',
    SK: 'sk', HU: 'hu', RO: 'ro', BG: 'bg', HR: 'hr', RS: 'sr', SE: 'sv', NO: 'no', FI: 'fi',
};

// Before 2026-10-10 onboarding's "my language isn't listed" saved English as
// the native language, so English learners ended up English→English (25
// accounts, none retained past day 1): translations in the language they're
// learning. This asks them, once per session until fixed, to set it right.
export function needsLanguageFix(settings: { onboarding_complete?: boolean; native_language?: string; learning_language?: string } | null | undefined): boolean {
    return !!settings && settings.onboarding_complete !== false
        && !!settings.native_language && settings.native_language === settings.learning_language;
}

// The prompt is a full-screen dialog: not over onboarding (which sets the languages itself),
// the AI-app consent page (it would cover Approve while the consent request expires), or the
// public share pages (/c/, /w/, /l/ — visitors there aren't in the app).
export function showsLanguageFixPrompt(pathname: string): boolean {
    return pathname !== '/profile-setup' && pathname !== '/oauth/consent' && !/^\/[cwl]\//.test(pathname);
}

const dismissKey = (userId: number | string) => `fix-languages-later:${userId}`;
// Fired on "Later", so other components (the dashboard tour) learn the prompt is gone.
const DISMISSED_EVENT = 'fix-languages-dismissed';

function isDismissed(userId: number | string | undefined): boolean {
    try { return userId != null && sessionStorage.getItem(dismissKey(userId)) === '1'; } catch { return false; }
}

// Whether the prompt is (or is about to be) on screen: the languages need fixing and the
// user hasn't pressed "Later" this session. The dashboard tour waits on this, not on
// needsLanguageFix alone, or "Later" would hold the tour back for good.
export function useLanguageFixPending(): boolean {
    const auth = useAuth();
    const userId = auth?.user?.id;
    const [dismissed, setDismissed] = useState(() => isDismissed(userId));
    useEffect(() => {
        setDismissed(isDismissed(userId));
        const onDismiss = () => setDismissed(true);
        window.addEventListener(DISMISSED_EVENT, onDismiss);
        return () => window.removeEventListener(DISMISSED_EVENT, onDismiss);
    }, [userId]);
    return needsLanguageFix(auth?.userSettings) && !dismissed;
}

const FixLanguagesPrompt: React.FC = () => {
    const { t } = useTranslation('setup');
    const auth = useAuth();
    const settings = auth?.userSettings;
    const userId = auth?.user?.id;
    const open = useLanguageFixPending();
    const [languages, setLanguages] = useState<SupportedLanguage[]>([]);
    const [learning, setLearning] = useState('');
    const [native, setNative] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);
    const guessed = useRef('');

    useEffect(() => {
        if (!open || languages.length > 0) return;
        globalSettingService.getSupportedLanguages().then(langs => {
            setLanguages(langs);
            const current = settings?.learning_language ?? '';
            setLearning(langs.some(l => l.code === current && isOfferedForLearning(l)) ? current : '');
            const country = (auth?.user?.registration_country ?? '').toUpperCase();
            const guess = COUNTRY_LANGUAGE[country];
            if (guess && guess !== current && langs.some(l => l.code === guess)) {
                setNative(guess);
                guessed.current = guess;
            }
            trackEvent('languages_fix_shown', { country: country || null, guessed: guess ?? null });
        }).catch(() => { /* no list, no prompt — retried on the next visit */ });
    }, [open, languages.length, settings?.learning_language, auth?.user?.registration_country]);

    if (!open || languages.length === 0 || !settings) return null;

    const later = () => {
        try { if (userId != null) sessionStorage.setItem(dismissKey(userId), '1'); } catch { /* ignore */ }
        trackEvent('languages_fix_later');
        window.dispatchEvent(new Event(DISMISSED_EVENT));
    };

    const save = async () => {
        setSaving(true);
        setError(false);
        try {
            await userSettingService.updateSettings({ native_language: native, learning_language: learning });
            trackEvent('languages_fix_saved', { native_language: native, learning_language: learning, kept_guess: native === guessed.current });
            const learningChanged = learning !== settings.learning_language;
            auth?.updateUserSettings({ ...settings, native_language: native, learning_language: learning });
            // Collections in context are filtered by learning language and only refetched on
            // request — same as ProfileTab after a learning-language change.
            if (learningChanged) await auth?.refreshUserData?.();
        } catch {
            setError(true);
        } finally {
            setSaving(false);
        }
    };

    const valid = !!learning && !!native && learning !== native;

    return (
        <div className="upgrade-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="fix-languages-title">
            <div className="upgrade-modal fix-languages">
                <h2 id="fix-languages-title" className="upgrade-modal-title">{t('fix_languages.title')}</h2>
                <p className="upgrade-modal-description">{t('fix_languages.text')}</p>
                <div className="fix-languages-field">
                    <div className="fix-languages-label">{t('fields.learning_language')}</div>
                    <LanguageSelect
                        languages={languages.filter(isOfferedForLearning)}
                        value={learning}
                        onChange={code => { setLearning(code); if (code === native) setNative(''); }}
                        placeholder={t('fields.learning_language_placeholder')}
                    />
                </div>
                <div className="fix-languages-field">
                    <div className="fix-languages-label">{t('fields.native_language')}</div>
                    <LanguageSelect
                        languages={languages.filter(l => l.code !== learning)}
                        value={native}
                        onChange={setNative}
                        placeholder={t('fields.native_language_placeholder')}
                    />
                </div>
                {error && <p className="fix-languages-error">{t('errors.save_failed')}</p>}
                <button type="button" className="fix-languages-save" onClick={save} disabled={!valid || saving}>
                    {saving ? t('buttons.saving') : t('fix_languages.save')}
                </button>
                <button type="button" className="fix-languages-later" onClick={later} disabled={saving}>
                    {t('fix_languages.later')}
                </button>
            </div>
        </div>
    );
};

export default FixLanguagesPrompt;
